import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Stack,
} from '@mui/material';
import Launch from '@mui/icons-material/Launch';
import ArrowForward from '@mui/icons-material/ArrowForward';
import { Link as RouterLink } from 'react-router-dom';

function Projects() {
  const { t } = useTranslation();

  const projects = [
    {
      key: 'haikei',
      internalUrl: '/haikei',
      year: '2025',
    },
    {
      key: 'spotify',
      internalUrl: '/spotify-analyzer',
      year: '2024',
    },
    {
      key: 'hft',
      internalUrl: '/hft-memoir',
      year: '2023',
    },
    {
      key: 'arutairu',
      internalUrl: '/arutairu',
      year: '2020',
    },
    {
      key: 'discuss',
      year: '2020',
    },
    {
      key: 'neo4j',
      internalUrl: '/neo4j-contacts',
      year: '2020',
    },
    {
      key: 'datasound',
      year: '2019',
    },
    {
      key: 'datastrophes',
      year: '2020',
    },
    {
      key: 'ecolepetitprince',
      year: '2020',
    },
    {
      key: 'datask',
      year: '2019',
    },
    {
      key: 'thesleeper',
      url: 'https://globalgamejam.org/2020/games/sleeper-7',
      year: '2020',
    },
    {
      key: 'tomskeys',
      url: 'https://globalgamejam.org/2019/games/toms-keys',
      year: '2019',
    },
    {
      key: 'iut',
      url: null,
      year: '2020',
    },
    {
      key: 'iot',
      url: 'https://xenom.altair-studios.fr/iot',
      year: '2018',
    },
    {
      key: 'turbostage',
      url: 'https://doc.altair-studios.fr/Turbostage/',
      year: '2019',
    },
    {
      key: 'drawzone',
      url: null,
      year: '2019',
    },
    {
      key: 'fileindexer',
      url: null,
      year: '2019',
    },
    {
      key: 'tsp',
      url: null,
      year: '2019',
    },
  ];

  return (
    <Box
      id="projects"
      component="section"
      sx={{
        py: { xs: 6, md: 10 },
        position: 'relative',
        overflowX: 'hidden',
        width: '100%',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, rgba(10, 10, 10, 0.75) 0%, rgba(10, 10, 10, 0.85) 100%)',
          backdropFilter: 'blur(20px)',
          zIndex: 0,
        },
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ mb: { xs: 4, md: 6 } }}>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3.5rem' },
              fontWeight: 500,
              mb: 1.5,
              color: '#ffffff',
              fontFamily: '"Excellence In Motion", sans-serif',
            }}
          >
            {t('projects.title')}
          </Typography>
        </Box>

        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {projects.map((project) => (
            <Grid item xs={12} sm={6} md={4} key={project.key}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <CardContent sx={{ flexGrow: 1, p: { xs: 2, sm: 2.5, md: 3 } }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '0.8rem',
                      color: '#ff6b6b',
                      mb: 2,
                      fontWeight: 700,
                      letterSpacing: '0.1em',
                    }}
                  >
                    {project.year}
                  </Typography>

                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: '1.25rem', md: '1.375rem' },
                      mb: 2,
                      color: '#ffffff',
                    }}
                  >
                    {t(`projects.${project.key}.title`)}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      color: '#d4d4d4',
                      lineHeight: 1.8,
                      mb: 3,
                      fontSize: '0.9375rem',
                      fontWeight: 400,
                    }}
                  >
                    {t(`projects.${project.key}.description`)}
                  </Typography>

                  <Stack direction="row" flexWrap="wrap" spacing={1} gap={1}>
                    {(t(`projects.${project.key}.tech`, { returnObjects: true }) || []).constructor === Array && 
                      t(`projects.${project.key}.tech`, { returnObjects: true }).map((tech, index) => (
                      <Chip
                        key={index}
                        label={tech}
                        size="small"
                        sx={{
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          background: index % 3 === 0
                            ? 'linear-gradient(135deg, rgba(255, 107, 107, 0.15), rgba(255, 107, 107, 0.25))'
                            : index % 3 === 1
                            ? 'linear-gradient(135deg, rgba(78, 205, 196, 0.15), rgba(78, 205, 196, 0.25))'
                            : 'linear-gradient(135deg, rgba(255, 217, 61, 0.15), rgba(255, 217, 61, 0.25))',
                          color: index % 3 === 0
                            ? '#ff8787'
                            : index % 3 === 1
                            ? '#4ecdc4'
                            : '#ffd93d',
                          border: index % 3 === 0
                            ? '1px solid rgba(255, 107, 107, 0.3)'
                            : index % 3 === 1
                            ? '1px solid rgba(78, 205, 196, 0.3)'
                            : '1px solid rgba(255, 217, 61, 0.3)',
                        }}
                      />
                    ))}
                  </Stack>
                </CardContent>

                {project.url && (
                  <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 }, pt: 0 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      endIcon={<Launch sx={{ fontSize: 16 }} />}
                      href={project.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      fullWidth
                      sx={{
                        borderWidth: 2,
                        '&:hover': {
                          borderWidth: 2,
                          background: 'linear-gradient(135deg, rgba(255, 107, 107, 0.1), rgba(78, 205, 196, 0.1))',
                        },
                      }}
                    >
                      {t('projects.viewProject')}
                    </Button>
                  </Box>
                )}

                {project.internalUrl && (
                  <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 }, pt: 0 }}>
                    <Button
                      component={RouterLink}
                      to={project.internalUrl}
                      variant="contained"
                      size="small"
                      endIcon={<ArrowForward sx={{ fontSize: 16 }} />}
                      fullWidth
                      sx={{
                        background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
                        color: '#ffffff !important',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #ff8787 0%, #ff6b6b 100%)',
                          color: '#ffffff !important',
                        },
                      }}
                    >
                      {t('projects.learnMore')}
                    </Button>
                  </Box>
                )}
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}

export default Projects;
